/**
 * Global composer state — lets any screen open the message composer sheet
 * without owning the sheet UI. The sheet itself is mounted once in
 * RootNavigator (GlobalComposerHost) and reads from this slice.
 *
 * Delivers half of the "inbox useless, compose prominent" thesis: the
 * compose surface is now globally accessible, no longer trapped inside
 * InboxScreen.
 */

export const OPEN_COMPOSER  = 'COMPOSER/OPEN';
export const CLOSE_COMPOSER = 'COMPOSER/CLOSE';
export const MARK_SENT      = 'COMPOSER/MARK_SENT';

export interface ComposerInitial {
  documentAttachments?: any[];
  replyToMessageId?: number;
  emailUpdateRequest?: { id: string; address: string };
  to?: string;
  subject?: string;
  body?: string;
}

export interface ComposerState {
  open: boolean;
  projectId: number | string | null;
  inboxEmail: string | null;
  initial: ComposerInitial;
  /** Bumped on every successful send so screens can re-fetch. */
  lastSentAt: number;
}

const initialState: ComposerState = {
  open: false,
  projectId: null,
  inboxEmail: null,
  initial: {},
  lastSentAt: 0,
};

export const openComposer = (args: {
  projectId: number | string;
  inboxEmail?: string | null;
  initial?: ComposerInitial;
}) => ({
  type: OPEN_COMPOSER,
  payload: {
    projectId: args.projectId,
    inboxEmail: args.inboxEmail ?? null,
    initial: args.initial ?? {},
  },
});

export const closeComposer = () => ({ type: CLOSE_COMPOSER });
export const markComposerSent = () => ({ type: MARK_SENT });

const composerReducer = (
  state = initialState,
  { type, payload }: { type: string; payload?: any },
): ComposerState => {
  switch (type) {
    case OPEN_COMPOSER:
      return {
        ...state,
        open: true,
        projectId: payload.projectId,
        inboxEmail: payload.inboxEmail,
        initial: payload.initial || {},
      };
    case CLOSE_COMPOSER:
      return { ...state, open: false, initial: {} };
    case MARK_SENT:
      return { ...state, lastSentAt: Date.now() };
    default:
      return state;
  }
};

export default composerReducer;
